/**
 * StoneListingsContent - Stone Investment themed Properties/Listings Page Content
 * 
 * Full listings page with Stone Investment's luxury portfolio aesthetic:
 * - Editorial hero section
 * - Greyscale colour palette
 * - Elegant filtering with cinematic transitions
 */

'use client';

import { useState, useMemo } from 'react';
import type { ListingOnChannel } from '@/lib/api/clients/listings';
import {
  ListingFilters,
  defaultFilters,
  transformListingToCardData,
} from '@/components/listings';
import type { ListingFiltersState, ListingCardData } from '@/components/listings';
import { StoneListingGrid } from './StoneListingGrid';

interface Props {
  listings: ListingOnChannel[];
  locale: string;
  messages: Record<string, unknown>;
  basePath: string;
  propertyTypes?: Array<{ id: string; name: string }>;
}

export function StoneListingsContent({
  listings,
  locale,
  messages,
  basePath,
  propertyTypes = [],
}: Props) {
  const [filters, setFilters] = useState<ListingFiltersState>(defaultFilters);
  
  // Transform API listings to card data
  const listingCardData = useMemo(() => {
    return listings
      .map(transformListingToCardData)
      .filter((l): l is ListingCardData => l !== null);
  }, [listings]);
  
  // Apply filters
  const filteredListings = useMemo(() => {
    return listingCardData
      .filter((listing) => {
        // Search filter
        if (filters.search) {
          const search = filters.search.toLowerCase();
          if (!listing.property.name.toLowerCase().includes(search)) {
            return false;
          }
        }
        
        // Listing type filter
        if (filters.listingType !== 'ALL' && listing.listingType !== filters.listingType) {
          return false;
        }
        
        // Status filter
        if (filters.status !== 'ALL' && listing.status !== filters.status) {
          return false;
        }
        
        // Price filter
        if (filters.minPrice !== null && (!listing.price || listing.price.amount < filters.minPrice)) {
          return false;
        }
        if (filters.maxPrice !== null && (!listing.price || listing.price.amount > filters.maxPrice)) {
          return false;
        }
        
        // Bedrooms filter
        if (filters.minBedrooms !== null && (listing.property.bedrooms === null || listing.property.bedrooms < filters.minBedrooms)) {
          return false;
        }
        
        // Bathrooms filter
        if (filters.minBathrooms !== null && (listing.property.bathrooms === null || listing.property.bathrooms < filters.minBathrooms)) {
          return false;
        }
        
        // Property type filter
        if (filters.propertyTypeId !== null && listing.propertyType?.id !== filters.propertyTypeId) {
          return false;
        }
        
        return true;
      })
      .sort((a, b) => {
        switch (filters.sortBy) {
          case 'newest':
            return (b.listedDate || '').localeCompare(a.listedDate || '');
          case 'price_asc':
            return (a.price?.amount || 0) - (b.price?.amount || 0);
          case 'price_desc':
            return (b.price?.amount || 0) - (a.price?.amount || 0);
          case 'bedrooms':
            return (b.property.bedrooms || 0) - (a.property.bedrooms || 0);
          default:
            return 0;
        }
      });
  }, [listingCardData, filters]);

  const stoneMessages = (messages.stone || {}) as Record<string, unknown>;
  const propertiesMessages = (stoneMessages.properties || messages.properties || {}) as Record<string, string>;
  const listingsMessages = (stoneMessages.listingsContent || {}) as Record<string, string>;
  const emptyMessage = propertiesMessages.noListings || listingsMessages.noPropertiesAvailable || 'No properties available';
  const emptyDescription = propertiesMessages.noListingsDescription || listingsMessages.noPropertiesDescription ||
    'Our portfolio is constantly evolving. Please check back soon or contact us for exclusive opportunities.';

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section - Stone Investment styled */}
      <section className="relative bg-stone-100 pt-32 pb-20 px-6 md:px-12">
        {/* Decorative border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-stone-300" />
        
        <div className="container mx-auto max-w-7xl">
          {/* Eyebrow */}
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-sans mb-6">
            {propertiesMessages.eyebrow || 'Portfolio'}
          </p>
          
          <h1 
            className="font-serif text-5xl md:text-6xl lg:text-7xl text-stone-900 mb-6 leading-[1.1]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {propertiesMessages.title || 'Property Collection'}
          </h1>
          
          <p 
            className="text-lg text-stone-600 max-w-2xl font-sans leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {propertiesMessages.subtitle || 
              'A curated selection of exceptional properties, each chosen for its character, location, and investment potential.'}
          </p>
          
          {/* Stats */}
            <div className="flex flex-wrap gap-12 mt-12 pt-8 border-t border-stone-300">
            <div className="flex flex-col">
              <span className="font-serif text-4xl text-stone-900">{listings.length}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-1">{propertiesMessages.properties || listingsMessages.properties || 'Properties'}</span>
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-4xl text-stone-900">
                {listings.filter(l => l.listing?.listingType === 'SALE').length}
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-1">{propertiesMessages.forSale || listingsMessages.forSale || 'For Sale'}</span>
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-4xl text-stone-700">
                {listings.filter(l => 
                  l.listing?.listingType === 'RENTAL' || 
                  l.listing?.listingType === 'HOLIDAY_RENTAL'
                ).length}
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-1">{propertiesMessages.forRent || listingsMessages.forRent || 'For Rent'}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Filters - Stone Investment styled */}
      <section className="sticky top-16 z-30 bg-white border-y border-stone-200 shadow-sm">
        <div className="container mx-auto max-w-7xl px-6 md:px-12 py-4">
          <ListingFilters
            filters={filters}
            onChange={setFilters}
            propertyTypes={propertyTypes}
            showAdvanced={true}
          />
        </div>
      </section>
      
      {/* Results */}
      <section className="py-16 px-6 md:px-12">
        <div className="container mx-auto max-w-7xl">
          {/* Results count */}
          <div className="flex items-center justify-between mb-12">
            <p className="text-stone-600 font-sans text-sm tracking-wide">
              {propertiesMessages.showing || 'Showing'} <span className="font-medium text-stone-900">{filteredListings.length}</span> 
              {filteredListings.length === 1 ? ` ${propertiesMessages.property || 'property'}` : ` ${propertiesMessages.properties || listingsMessages.properties || 'properties'}`}
              {filters.listingType !== 'ALL' && (
                <span className="text-stone-500 ml-2">
                  · {filters.listingType.replace('_', ' ').toLowerCase()}
                </span>
              )}
            </p>
          </div>
          
          {/* Listings Grid */}
          <StoneListingGrid
            listings={filteredListings}
            basePath={basePath}
            variant="grid"
            columns={2}
            showAgent={false}
            showAgentAvatar={false}
            showStatus={true}
            locale={locale}
            emptyMessage={emptyMessage}
            emptyDescription={emptyDescription}
          />
        </div>
      </section>
    </div>
  );
}

export default StoneListingsContent;
